import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import quarkusApi from '../api/quarkusApi';

dayjs.extend(customParseFormat);

const DATE_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD'];
const DATE_TIME_FORMATS = ['DD/MM/YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm'];

const statusToColumnId = {
  TODO: 'TO_DO',
  DOING: 'IN_PROGRESS',
  DONE: 'DONE',
};

const columnIdToStatus = {
  TO_DO: 'TODO',
  IN_PROGRESS: 'DOING',
  DONE: 'DONE',
};

const toMessage = (data) => (typeof data === 'string' ? data : 'Ocorreu um erro. Tente novamente.');

const parseDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, DATE_FORMATS);
  return parsed.isValid() ? parsed : null;
};

const parseDateTime = (value) => {
  if (!value) return '';
  const parsed = dayjs(value, DATE_TIME_FORMATS);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '';
};

const mapTaskFromApi = (dto) => ({
  id: dto.id,
  title: dto.title,
  description: dto.description || '',
  executionDate: parseDate(dto.executionDate),
  time: null,
  recurringTaskId: dto.isRecurrentTask ? dto.recurrentTaskId : undefined,
  sequence: dto.order !== null ? dto.order : undefined,
  columnId: statusToColumnId[dto.status] || 'TO_DO',
  notes: (dto.annotations || []).map((annotation, index) => ({
    id: index + 1,
    text: annotation.description,
    date: parseDateTime(annotation.createdAt),
  })),
});

export default function useClinicTasks() {
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // `showLoading` false = recarga silenciosa, sem piscar o overlay numa lista já exibida.
  const fetchTasks = useCallback(
    (showLoading = true) => {
      if (showLoading) {
        setLoading(true);
      }
      return quarkusApi
        .get('/clinic-tasks', {
          headers: { Authorization: sessionStorage.getItem('token') },
        })
        .then((returnFromApi) => {
          setTasks((returnFromApi.data || []).map(mapTaskFromApi));
        })
        .catch((returnFromApi) => {
          setShowErrorMessage(true);
          setErrorMessage(toMessage(returnFromApi.response?.data));
          console.error('ops! ocorreu um erro' + returnFromApi);
          if (returnFromApi.status === 401) {
            navigate('/expired-session');
          }
        })
        .finally(() => {
          if (showLoading) {
            setLoading(false);
          }
        });
    },
    [navigate],
  );

  const addTask = (taskForm) => {
    setLoading(true);
    const payload = {
      title: taskForm.title,
      description: taskForm.description,
      executionDate: taskForm.executionDate
        ? dayjs(taskForm.executionDate).format('DD/MM/YYYY')
        : null,
    };
    return quarkusApi
      .post('/clinic-tasks', payload, {
        headers: { Authorization: sessionStorage.getItem('token') },
      })
      .then((returnFromApi) => {
        setSuccessMessage(toMessage(returnFromApi.data));
        setShowSuccessMessage(true);
        return true;
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toMessage(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        return false;
      })
      .finally(() => setLoading(false));
  };

  const updateTask = (id, taskForm) => {
    setLoading(true);
    const payload = {
      title: taskForm.title,
      description: taskForm.description,
      executionDate: taskForm.executionDate
        ? dayjs(taskForm.executionDate).format('DD/MM/YYYY')
        : null,
    };
    return quarkusApi
      .put(`/clinic-tasks/${id}`, payload, {
        headers: { Authorization: sessionStorage.getItem('token') },
      })
      .then((returnFromApi) => {
        setSuccessMessage(toMessage(returnFromApi.data));
        setShowSuccessMessage(true);
        return true;
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toMessage(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        return false;
      })
      .finally(() => setLoading(false));
  };

  const changeTaskStatus = (id, columnId) => {
    setLoading(true);
    return quarkusApi
      .put(
        `/clinic-tasks/${id}/status`,
        { status: columnIdToStatus[columnId] },
        { headers: { Authorization: sessionStorage.getItem('token') } },
      )
      .then(() => true)
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toMessage(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        return false;
      })
      .finally(() => setLoading(false));
  };

  const deleteTask = (id) => {
    setLoading(true);
    return quarkusApi
      .delete(`/clinic-tasks/${id}`, {
        headers: { Authorization: sessionStorage.getItem('token') },
      })
      .then((returnFromApi) => {
        setSuccessMessage(toMessage(returnFromApi.data));
        setShowSuccessMessage(true);
        return true;
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toMessage(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        return false;
      })
      .finally(() => setLoading(false));
  };

  const addAnnotation = (taskId, text) => {
    setLoading(true);
    return quarkusApi
      .post(
        `/clinic-tasks/${taskId}/annotations`,
        { description: text },
        { headers: { Authorization: sessionStorage.getItem('token') } },
      )
      .then((returnFromApi) => {
        setSuccessMessage(toMessage(returnFromApi.data));
        setShowSuccessMessage(true);
        return true;
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toMessage(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        return false;
      })
      .finally(() => setLoading(false));
  };

  return {
    tasks,
    setTasks,
    isLoading,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
    fetchTasks,
    addTask,
    updateTask,
    changeTaskStatus,
    deleteTask,
    addAnnotation,
  };
}
